import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentCanDeactivate } from '@app/can-deactivate.guard';
import { RecordSelectModalComponent } from '@app/components/record-select/record-select-modal.component';
import { NoteUpdate } from '@app/models/note.model';
import { Profile } from '@app/models/profile.model';
import {
  NameLocalStorage,
  RecordField,
  RecordTitle,
} from '@app/models/record.model';
import { Staff } from '@app/models/staff.model';
import { User } from '@app/models/user.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService } from '@app/services/api.service';
import { DialogService, fullscreenConfig } from '@app/services/dialog.service';
import { MessageService } from '@app/services/message.service';
import { RecordService } from '@app/services/record.service';
import { ScheduleService } from '@app/services/schedule.service';
import { PURPOSES } from '@app/shared/enum/record-list.enum';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { RecordSelectors } from '@app/store/record/record.selectors';
import { LibNoteService, buddyUtils } from '@care-connect-japan/ckweblib';
import { Store } from '@ngxs/store';
import * as moment from 'moment';
import { WebviewService } from '@app/services/webview.service';
import { filter, mergeMap } from 'rxjs';
import { TriggerHistory } from '@app/store/schedule/schedule.classes';
import { NoteBlobService } from '@app/services/noteblob.service';
import { DataSelectors } from '@app/store/data/data.selectors';

@Component({
  selector: 'app-record-add',
  templateUrl: './record-add.component.html',
  styleUrls: ['./record-add.component.scss'],
})
export class RecordAddComponent implements OnInit, ComponentCanDeactivate, OnDestroy {
  displayName = '記録を入力';

  staffList!: Staff[];
  profile!: Profile | null;
  user!: User;
  records!: RecordField[];
  form!: FormGroup;
  isPersonal: boolean = false;
  historyBackWard = () => history.back();
  private canLeave: boolean = false;

  isLoading: boolean = false;

  constructor(
    public scheduleService: ScheduleService,
    public recordService: RecordService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private matDialog: MatDialog,
    private accesslog: AccesslogService,
    private message: MessageService,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private libNoteService: LibNoteService,
    protected noteblobService: NoteBlobService,
    private webviewService: WebviewService
  ) {
  }
  ngOnDestroy(): void {
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
  }
  pagingOffset = 1;
  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.isPersonal = params['personal'] === 'true';
    });
    this.profile = this.store.selectSnapshot(AuthSelectors.profile);
    this.webviewService.kidsWarningBeforeChangePageIndicator(true);
    this.route.data.subscribe((data) => {
      this.staffList = data['staffList'];

      const record = data['record'];
      this.user = record.user;
      this.records = record.items ?? [];

      this.form = this.fb.group({
        records: this.fb.array(
          this.records?.map((record) =>
            this.recordService.setRecordForm(this.profile, record)
          ) ?? []
        ),
      });
    });

    const setting = this.store.selectSnapshot(RecordSelectors.setting);
    const displaySettingDateJson: any = localStorage.getItem(
      NameLocalStorage.DISPLAY_SETTING_DATE
    );
    const displaySettingDateStorage =
      JSON.parse(displaySettingDateJson) || [];
    let displaySettingDate: any = [];
    if (displaySettingDateStorage.length > 0) {
      const findIndex =
        displaySettingDateStorage.findIndex(
          (f: any) => f.ckbnId === setting?.ckbnId
        ) ?? -1;
      if (findIndex !== -1) {
        displaySettingDate = displaySettingDateStorage[findIndex];
      }
    }
  }

  canDeactivate() {
    return this.canLeave;
  }

  get recordsForm() {
    return this.form.get('records') as FormArray;
  }

  getRecordForm(index: number) {
    return this.recordsForm.at(index) as FormGroup;
  }

  delete(record: RecordField, index: number) {
    this.recordService.delete(record, this.user.name).subscribe(() => {
      this.records = this.records.map((item, i) => {
        return i === index ? { ...item, delstat: 1 } : item;
      });
    });
  }

  addRecord(): void {
    this.matDialog
      .open(RecordSelectModalComponent, fullscreenConfig)
      .afterClosed()
      .pipe(
        filter((selected) => !!selected),
        mergeMap((selected: RecordTitle[]) => {
          const payload = {
            sdate: new Date(),
            objcd: this.user.daicd,
            titleSetKeys: this.recordService.getTitleSetKeys(selected),
          };
          return this.apiService.getNoteListDefault(payload);
        })
      )
      .subscribe((res) => {
        res.forEach((note) => {
          const record = { ...note, delstat: 0, lockflg: 0 };
          this.records = [...this.records, record];

          const control = this.recordService.setRecordForm(
            this.profile,
            record
          );
          this.recordsForm.push(control);
        });
      });
  }

  async onSubmit(): Promise<void> {
    this.isLoading = true;
    const noteDatas = this.libNoteService.getNoteData();

    if (this.recordService.isValidationError(noteDatas)) return;

    const findNoteData = this.recordService.findNoteData(noteDatas);
    const getStaffName = this.scheduleService.getStaffName(this.staffList);
    const curObjectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    const curUserList = this.store.selectSnapshot(DataSelectors.userList(curObjectcd));
    const userInfo = curUserList.find((user) => user.daicd === this.user.daicd) as any;
    const ownunitcd = userInfo 
      ? `${userInfo.corpcd}${userInfo.officecd}${userInfo.blockcd}00${userInfo.unitcd}`
      : curObjectcd;
    const { daicd: objcd, name: objnm } = this.user;
    const payload: NoteUpdate[] = [];
    this.records.forEach((record, i) => {
      if (this.recordService.isDeleteAddedRecord(record)) return; // continue

      const noteData = findNoteData(record);
      const operation = this.recordService.getOperation(record.noteid);
      const note = {
        ...record,
        ...noteData,
        ...this.recordService.getFormValue(
          this.getRecordForm(i).value,
          getStaffName(record)
        ),
        objkbn: '00',
        objcd,
        objnm,
        dataV2Contents: undefined,
        tp: undefined,
        titles: undefined,
        ownunitcd
      };
      const displayName = this.accesslog.getKidsDisplayName(this.displayName);

      payload.push({ operation, note, displayName });
    });
    const _payload = await this.noteblobService.createNoteBlobPayload(payload);
    this.apiService
      .setNoteList(_payload)
      .pipe(filter((res) => !!res))
      .subscribe((res) => {
        this.isLoading = false;
        const errorList = res
          .filter((item) => !!item.error)
          .map((item) => this.message.getErrorMessage(item.error!));

        if (errorList.length > 0) {
          this.dialog.message(errorList.join('\n'));
        } else if (res.length > 0) {
          this.snackBar.open('記録を保存しました');
        }

        if (errorList.length !== res.length) {
          this.store.dispatch(new TriggerHistory());
          this.redirect();
        }
      });
  }

  private redirect() {
    this.canLeave = true;
    this.webviewService.kidsWarningBeforeChangePageIndicator(false);
    this.store.dispatch(new TriggerHistory)
    this.router.navigate(['', { outlets: { edit: null } }], { replaceUrl: true, queryParams: { personal: this.isPersonal || null } }).finally(() => this.historyBackWard())
  }

  createPayload(purpose: PURPOSES = PURPOSES.GET_RECORD_LIST, sDate?: Date | null, eDate?: Date | null) {
    let setting = buddyUtils.default.deepCloneExceptParent({
      ...this.store.selectSnapshot(RecordSelectors.setting),
    });

    switch (purpose) {
      case PURPOSES.GET_RECORD_LIST:
        const displayDate = this.store.selectSnapshot(RecordSelectors.displayDate);
        const edate = setting?.edate ?? displayDate?.edate;
        const sdate = setting?.sdate ?? displayDate?.sdate;
        let filterSdate =
          moment(edate || new Date())
            .subtract(this.pagingOffset, 'day')
            .toDate() < moment(sdate || new Date()).toDate()
            ? moment(sdate || new Date()).toDate()
            : moment(edate || new Date())
              .subtract(this.pagingOffset, 'day')
              .toDate();
        setting.sdate = filterSdate;
        setting.edate = moment(edate || new Date())
          .endOf('day')
          .toDate();
        break;
      default:
        break;
    }
    return setting;
  }
}
